
import { jwtDecode } from 'jwt-decode';
import { v4 as uuidv4 } from 'uuid';

import { getURLWithoutParams } from '../connection';
import { parseURLParams } from '../util';
import { checkIfNotReactNative } from '../util/checkOS';

const CONFERENCE_SESSION_ID_KEY = 'meethour.conference-session-id';
let conferenceSessionId;

/**
 * Normalizes a JWT user identifier so numeric and string claims compare the
 * same way throughout the signaling stack.
 *
 * @param {*} value - Candidate user identifier.
 * @returns {string|undefined}
 */
function _normalizeUserId(value) {
    if (typeof value === 'number' || typeof value === 'string') {
        const normalized = String(value).trim();

        return normalized || undefined;
    }
}

/**
 * Decodes a token without validating it. Token validation remains a server
 * responsibility; this value is only used to opt into session-persistence
 * signaling.
 *
 * @param {string|undefined} token - Encoded JWT.
 * @returns {Object|undefined}
 */
function _decodeToken(token) {
    if (typeof token !== 'string' || !token) {
        return undefined;
    }

    try {
        return jwtDecode(token);
    } catch {
        return undefined;
    }
}

/**
 * Returns the stable id of the signed in person, used to recognize every
 * endpoint of that person across reconnects, tabs and devices. The meeting JWT
 * is the authoritative source, matching what Prosody derives from the verified
 * session. Access-token flows must receive a meeting token whose verified
 * account id is copied into context.user.id or user_id by the token service.
 *
 * Guests have no such id, which is what keeps this feature out of their way.
 *
 * @param {Object} state - Redux state.
 * @returns {string|undefined}
 */
export function getPersistentUserId(state: Object) {
    const jwtState = state['features/base/jwt'] || {};
    const meetingTokenPayload = _decodeToken(jwtState.jwt || jwtState.jwtData);

    return _normalizeUserId(meetingTokenPayload?.context?.user?.id)
        || _normalizeUserId(meetingTokenPayload?.user_id);
}

/**
 * Returns an id that remains stable for reconnects in this app tab/process but
 * differs between independently opened conference clients.
 *
 * @returns {string}
 */
export function getConferenceSessionId() {
    if (conferenceSessionId) {
        return conferenceSessionId;
    }

    try {
        conferenceSessionId = globalThis.sessionStorage?.getItem(CONFERENCE_SESSION_ID_KEY);
    } catch {
        // Storage can be unavailable in privacy-restricted browser contexts.
    }

    if (!conferenceSessionId) {
        conferenceSessionId = uuidv4();

        try {
            globalThis.sessionStorage?.setItem(CONFERENCE_SESSION_ID_KEY, conferenceSessionId);
        } catch {
            // The in-memory id still provides reconnect persistence in-process.
        }
    }

    return conferenceSessionId;
}

/**
 * Retrieves the JSON Web Token (JWT), if any, defined by a specific
 * {@link URL}.
 *
 * @param {URL} url - The {@code URL} to parse and retrieve the JSON Web Token
 * (JWT), if any, from.
 * @returns {string} The JSON Web Token (JWT), if any, defined by the specified
 * {@code url}; otherwise, {@code undefined}.
 */
export function parseJWTFromURLParams(url: URL = window.location) {
    return parseURLParams(url, true, 'search').mt;
}

/**
 * Retrieves the JSON Web Token (JWT), if any, defined by a specific
 * {@link URL}.
 *
 * @param {URL} url - The {@code URL} to parse and retrieve the JSON Web Token
 * (JWT), if any, from.
 * @returns {string} The JSON Web Token (JWT), if any, defined by the specified
 * {@code url}; otherwise, {@code undefined}.
 */
export function parseURLParamsBAck(url: URL) {
    return parseURLParams(url, true, 'search').goBack;
}

/**
 * Retrieves the JSON Web Token (JWT), if any, defined by a specific
 * {@link URL}.
 *
 * @param {URL} url - The {@code URL} to parse and retrieve the JSON Web Token
 * (JWT), if any, from.
 * @returns {string} The JSON Web Token (JWT), if any, defined by the specified
 * {@code url}; otherwise, {@code undefined}.
 */
export function parseRETURNURLFromURLParams(url: URL) {
    return parseURLParams(url, true, 'search').return_url ?? parseURLParams(url, true, 'search').return_url;
}

/**
 * Returns the user name after decoding the jwt.
 *
 * @param {Object} state - The app state.
 * @returns {string}
 */
/**
 * The stage role the local user's token claims, or undefined for a guest or a
 * token that only names a plain participant.
 *
 * Mirrors what the server honours when it decides where a joining user goes
 * (mod_visitors_role_based: is_token_moderator and get_stage_ids read
 * context.user.moderator / owner / role). The app's own top-level
 * {@code moderator} claim is accepted as well, since the rest of the client
 * already treats it as moderator. Informational only -- the server decides.
 *
 * @param {Object} state - The redux state.
 * @returns {('host'|'moderator'|'panelist'|'presenter'|undefined)}
 */
export function getTokenStageRole(state: Object) {
    const jwtState = state['features/base/jwt'] || {};
    const user = jwtState.user || {};

    if (user.owner === true || user.role === 'owner') {
        return 'host';
    }

    if (user.moderator === true || jwtState.moderator === true || user.role === 'moderator') {
        return 'moderator';
    }

    if (user.role === 'panelist' || user.role === 'presenter') {
        return user.role;
    }

    return undefined;
}

export function getJwtName(state: Object) {
    const { user } = state['features/base/jwt'];

    return user?.name;
}

/**
 * Reads one key of the meeting's settings as carried by the token.
 *
 * A moderator's token puts them in {@code meeting.settings} (stored as
 * {@code confSettings}); a guest gets the same object back from the getjwt call
 * (stored under {@code meetingDetails.settings}). Read in that order, the way
 * focus-mode does, so both kinds of token see the same switches.
 *
 * @param {Object} state - The redux state.
 * @param {string} key - The setting name, e.g. {@code 'ENABLE_BACKSTAGE_MODE'}.
 * @returns {*} The raw value, or undefined when the token does not carry it.
 */
export function getMeetingSetting(state: Object, key: string) {
    const jwt = state['features/base/jwt'] || {};

    return jwt.confSettings?.[key] ?? jwt.meetingDetails?.settings?.[key];
}

/**
 * Whether a meeting setting is switched on. The backend writes these as
 * {@code 1}/{@code 0}, but a stringified or boolean value means the same
 * thing. A setting the token does not carry at all is {@code defaultValue}:
 * off, unless the caller says the feature is on until the backend switches it
 * off.
 *
 * @param {Object} state - The redux state.
 * @param {string} key - The setting name.
 * @param {boolean} [defaultValue] - What a missing setting means.
 * @returns {boolean}
 */
export function isMeetingSettingEnabled(state: Object, key: string, defaultValue: boolean = false) {
    const value = getMeetingSetting(state, key);

    if (value === undefined || value === null) {
        return defaultValue;
    }

    return value === 1 || value === '1' || value === true;
}

export const removeUrlState = () => (dispatch: Function, getState: Function) => {
    const { locationURL } = getState()['features/base/connection'];

    if (checkIfNotReactNative()) {
        const { history, location } = window;

        if (history
            && location
            && history.length
            && typeof history.replaceState === 'function' && Boolean(parseURLParams(locationURL, true, 'search')?.mt
            || parseURLParams(locationURL, true, 'search')?.access_token || parseURLParams(locationURL, true, 'search')?.pcode)) {
            const replacement = getURLWithoutParams(location);
            const isInIframe = window.self !== window.top;

            if (location !== replacement && !isInIframe) {
                history.replaceState(
                history.state,
                (document && document.title) || '',
                replacement);
            }
        }
    }
};
